/**
 * Aritmética de paginación, compartida por los listados (ADR 0078).
 *
 * Vivía en `paginas/produccion/tablero.ts`: son los bordes que no se ven
 * mirando la pantalla —la última página de un total exacto, o qué página queda
 * al filtrar— y que **no fallan**, sino que muestran una tabla vacía con el
 * paginador diciendo que hay filas.
 */

/** Los tamaños que ofrece `imprenta` en el selector de su tabla. */
export const TAMANOS_DE_PAGINA = [5, 10, 20, 50] as const;

/** Cuántas páginas hay. Siempre al menos una: «Página 1 de 0» no se lee bien. */
export function totalPaginas(total: number, tamano: number): number {
  return Math.max(1, Math.ceil(total / tamano));
}

/** El recorte de una página. Más allá del final devuelve vacío, no el último. */
export function paginar<T>(items: readonly T[], pagina: number, tamano: number): T[] {
  const desde = (pagina - 1) * tamano;
  return items.slice(desde, desde + tamano);
}

/**
 * La página que corresponde mirar, recortada a lo que existe.
 *
 * Se filtra estando en la página 7 y el listado pasa a tener 2: sin esto la
 * tabla queda **vacía** con el paginador diciendo que hay filas, y se lee como
 * «el filtro no encontró nada».
 */
export function paginaValida(pagina: number, total: number, tamano: number): number {
  return Math.min(Math.max(1, pagina), totalPaginas(total, tamano));
}

/**
 * «Mostrando 11–20 de 47». El rango que se está viendo, en base 1.
 *
 * Con la lista vacía es `0–0 de 0` y no `1–0`: el primer número es la posición
 * de una fila que no existe.
 */
export function rangoVisible(
  pagina: number,
  tamano: number,
  total: number,
): { desde: number; hasta: number } {
  if (total === 0) return { desde: 0, hasta: 0 };
  const desde = (pagina - 1) * tamano + 1;
  return { desde, hasta: Math.min(pagina * tamano, total) };
}
